/**
 * Starter HTML that mirrors a classic "Construction Bid Proposal" layout
 * (blue section rules, owner/contractor columns, materials grid, costs table).
 * Used when Match file is an image / non-Nettles reference.
 */

export function buildConstructionBidStarter(specs = {}, officeLine = "") {
  const ph = (name, value = "") =>
    `<span class="f" contenteditable="true" data-ph="${name}">${value || ""}</span>`;

  const project =
    specs.projectName ||
    specs.subject ||
    "";
  const total = specs.basePrice ? `$${specs.basePrice}` : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<title>Construction Bid Proposal</title>
<style>
  @page { size: letter; margin: 0.65in; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    font-size: 12.5px;
    line-height: 1.45;
    background: #fff;
  }
  .page { max-width: 8.2in; margin: 0 auto; padding: 0.35in 0.5in 0.6in; }
  .title {
    color: #2f6fed;
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 6px;
    padding-bottom: 4px;
    border-bottom: 2px solid #2f6fed;
  }
  .subject { margin: 10px 0 14px; font-size: 13px; }
  .parties {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 18px;
  }
  .parties p { margin: 0 0 2px; }
  h2.sec {
    color: #2f6fed;
    font-size: 16px;
    font-weight: 700;
    margin: 18px 0 6px;
    padding-bottom: 3px;
    border-bottom: 1.5px solid #2f6fed;
  }
  .materials {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 28px;
    padding-left: 4px;
  }
  .materials div::before { content: "• "; }
  table.costs {
    width: 100%;
    border-collapse: collapse;
    margin-top: 6px;
  }
  table.costs td {
    border: 1px solid #333;
    padding: 6px 10px;
    vertical-align: top;
  }
  table.costs tr.total td { font-weight: 700; }
  .closing { margin-top: 16px; }
  .sig { margin-top: 10px; }
  .f {
    min-width: 1.2em;
    outline: none;
    border-bottom: 1px dashed #9bb8f0;
  }
  .f:empty::before {
    content: attr(data-ph);
    color: #9aa3b5;
    font-style: italic;
  }
  .office { margin-top: 22px; font-size: 11px; color: #555; }
  @media print {
    .f { border-bottom: none; }
    .f:empty::before { content: ""; }
  }
</style>
</head>
<body>
<div class="page">
  <h1 class="title">Construction Bid Proposal</h1>
  <p class="subject">Bid for ${ph("project / subject", project)}</p>

  <div class="parties">
    <div>
      <p>${ph("owner name")}</p>
      <p>${ph("owner title", "Owner")}</p>
      <p>${ph("owner company")}</p>
      <p>${ph("owner address")}</p>
    </div>
    <div>
      <p>${ph("contractor name")}</p>
      <p>${ph("contractor title", "Contractor")}</p>
      <p>${ph("contractor company")}</p>
      <p>${ph("contractor address")}</p>
    </div>
  </div>

  <h2 class="sec">Scope</h2>
  <p>${ph("scope paragraph", specs.system ? `This proposal covers ${specs.system}${specs.quantity ? ` for approximately ${specs.quantity} SF` : ""}${specs.basePrice ? `. Projected cost $${specs.basePrice}` : ""}.` : "")}</p>

  <h2 class="sec">Materials</h2>
  <div class="materials">
    <div>${ph("material 1")}</div>
    <div>${ph("material 2")}</div>
    <div>${ph("material 3")}</div>
    <div>${ph("material 4")}</div>
    <div>${ph("material 5")}</div>
    <div>${ph("material 6")}</div>
    <div>${ph("material 7")}</div>
    <div>${ph("material 8")}</div>
  </div>

  <h2 class="sec">Labor</h2>
  <p>${ph("labor line 1")}</p>
  <p>${ph("labor line 2")}</p>

  <h2 class="sec">Costs</h2>
  <table class="costs">
    <tr><td>Material</td><td></td><td>${ph("material cost")}</td></tr>
    <tr><td>Labor</td><td></td><td>${ph("labor cost")}</td></tr>
    <tr><td>Permits</td><td></td><td>${ph("permits cost")}</td></tr>
    <tr><td>Overview</td><td></td><td>${ph("overview cost")}</td></tr>
    <tr class="total"><td>Total Cost:</td><td></td><td>${ph("total cost", total)}</td></tr>
  </table>

  <h2 class="sec">Time Table</h2>
  <p>${ph("time table paragraph")}</p>

  <p class="closing">${ph("closing statement", "We take pride in our work and attention to detail. Contact me directly for further information:")}</p>
  <div class="sig">
    <p>${ph("contact name")}</p>
    <p>${ph("contact phone")}</p>
    <p>${ph("contact email")}</p>
  </div>
  ${officeLine ? `<p class="office">${officeLine}</p>` : ""}
</div>
</body>
</html>`;
}

export const MATCH_LAYOUT_SYSTEM = `You create branded proposal/report HTML templates by CLONING a reference layout.

CRITICAL when a reference image or reference HTML is provided:
1. Do NOT use the Nettles "BUDGET PROPOSAL" / house proposal_template layout.
2. Recreate the SAME visual structure as the reference: title style, section headings, columns, lists, tables, spacing, colors.
3. Produce one self-contained HTML document (inline CSS) that looks like the reference when opened in a browser.
4. Make key fields editable with: <span class="f" contenteditable="true" data-ph="label">value or empty</span>
5. Fill values only when the user provided them; otherwise leave empty editable spans.
6. Return FULL HTML in a \`\`\`html fence, then \`\`\`json {"reviewerNotes":["..."]}.

For a Construction Bid Proposal style reference typically include:
- Blue title with full-width blue underline
- Subject / bid line
- Two-column Owner | Contractor block
- Sections with blue underlined headings: Scope, Materials, Labor, Costs, Time Table
- Materials as two-column bullets
- Costs as a bordered table with Total Cost row
- Closing + contact block
`;
